import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui/useUI.ts';
import { useExport } from '@/hooks/ui/useExport';
import { useTableSearch } from '@/hooks/ui/table/useTableSearch.ts';
import { formatDateToYYYYMMDD, calculateDateRange, toCapitalCase } from '@/utils/index.ts';
import { attendanceService } from '@/api/domains/attendance';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table.tsx';
import type {
  MonthlyReportDay,
  MonthlyCut,
  LC_GetMonthlyReportIn,
  EmployeeAttendanceCompany,
} from '@/api/domains/attendance';
import type { MonthlyAttendanceFilters, RangeType } from '@/types/attendanceFilter.types.ts';

// Año de referencia arbitrario para extraer nombres de meses
// El año no afecta el formato del nombre del mes en toLocaleString
const REFERENCE_YEAR_FOR_MONTH_NAMES = 2000;

// Retardo antes de disparar la búsqueda automática al cambiar un filtro (ASIS-5607).
// Evita una consulta por cada paso de la cascada Empresa -> Año -> Mes -> Descripción.
const AUTO_SEARCH_DEBOUNCE_MS = 300;

// Inicial del día en español según getDay(). Se deriva del índice y no del nombre porque
// "Martes" y "Miércoles" comparten inicial y el diseño usa X para miércoles (ASIS-5606).
const DAY_INITIALS = ['D', 'L', 'M', 'X', 'J', 'V', 'S'] as const;

/**
 * Formatea la columna Fecha del diseño: "15/06/2026 (L)".
 * `date` llega ya en dd/mm/yyyy desde el mapper.
 */
export const formatDateWithDayInitial = (date: string): string => {
  const [day, month, year] = date.split('/');
  if (!day || !month || !year) return date;

  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(parsed.getTime())) return date;

  return `${date} (${DAY_INITIALS[parsed.getDay()]})`;
};

const getCutoffYearMonth = (cutoff: MonthlyCut) => {
  if (cutoff.endDate) {
    const parts = cutoff.endDate.split('/');
    if (parts.length === 3) {
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (!isNaN(month) && !isNaN(year)) {
        return { year, month };
      }
    }
  }
  return { year: cutoff.year, month: cutoff.month };
};

export const validateCanSearch = (filters: MonthlyAttendanceFilters): boolean => {
  if (filters.searchMode === 'MONTHLY') {
    return !!(filters.companyId && filters.year && filters.month && filters.cutoffId);
  }
  return !!(filters.dateFrom && filters.dateTo);
};

export const shouldLoadCutoffs = (
  filters: MonthlyAttendanceFilters,
): boolean =>
  filters.searchMode === 'MONTHLY';

export const useMonthlyAttendance = () => {
  const { t: translate } = useTranslation();
  const { openModal, showError, showApiError } = useUI();
  
  const todayDate = formatDateToYYYYMMDD(new Date());
  
  const [filters, setFilters] = useState<MonthlyAttendanceFilters>({
    searchMode: 'MONTHLY',
    formatType: 'HH:MM:SS',
    dateFrom: todayDate,
    dateTo: todayDate,
    rangeType: 'TODAY',
  });
  
  const [rows, setRows] = useState<MonthlyReportDay[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [employeeInfo, setEmployeeInfo] = useState({ name: '', id: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const [allCutoffs, setAllCutoffs] = useState<MonthlyCut[]>([]);
  const [loadingCutoffs, setLoadingCutoffs] = useState(false);
  const [companies, setCompanies] = useState<EmployeeAttendanceCompany[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [noCutsMessage, setNoCutsMessage] = useState<string | null>(null);
  const isSearchingRef = useRef(false);
  const isLoadingCutoffsRef = useRef(false);
  const searchModeRef = useRef(filters.searchMode);
  // Espejo de las utilidades cuya identidad puede cambiar entre renders, para poder usarlas
  // dentro de efectos de montaje sin declararlas como dependencias.
  const translateRef = useRef(translate);
  const showApiErrorRef = useRef(showApiError);

  useEffect(() => {
    translateRef.current = translate;
    showApiErrorRef.current = showApiError;
  });
  // Empresa cuyos cortes ya están cargados: evita recargar en cada render y permite
  // detectar el cambio de empresa para reposicionar la cascada Año/Mes/Descripción.
  const loadedCutoffsCompanyRef = useRef<number | null>(null);

  useEffect(() => {
    searchModeRef.current = filters.searchMode;
  }, [filters.searchMode]);
  
  const { debouncedSearchText, setSearchText } = useTableSearch({
    onDebouncedChange: () => setPage(1),
  });

  // Filter cutoffs to only show last 60 months
  const cutoffsWithinRange = useMemo(() => {
    const now = new Date();
    // 59 months ago gives us 60 months total including current
    const cutoffDate = new Date(now.getFullYear(), now.getMonth() - 59, 1);
    
    return allCutoffs.filter(cutoff => {
      const { year, month } = getCutoffYearMonth(cutoff);
      const isInvalid = year === null || year === undefined || 
        month === null || month === undefined;
      if (isInvalid) return false;
      const cutoffMonthDate = new Date(year, month - 1, 1);
      return cutoffMonthDate >= cutoffDate;
    });
  }, [allCutoffs]);

  const years = useMemo(() => {
    // Extract unique years from filtered cutoffs
    const uniqueYears = Array.from(new Set(cutoffsWithinRange
      .map(c => getCutoffYearMonth(c).year)
      .filter((y): y is number => y !== null && y !== undefined)));
    
    return uniqueYears.sort((a, b) => b - a).map(y => ({ value: String(y), label: String(y) }));
  }, [cutoffsWithinRange]);

  const months = useMemo(() => {
    if (!filters.year) return [];

    // Agrupa por mes/año, solo una opción por mes
    const uniqueMonthYear = new Map<number, number>();
    cutoffsWithinRange.forEach(cutoff => {
      const { year, month } = getCutoffYearMonth(cutoff);
      if (year === filters.year && month !== null && month !== undefined) {
        uniqueMonthYear.set(month, year);
      }
    });

    return Array.from(uniqueMonthYear.keys())
      .sort((a, b) => a - b)
      .map(m => {
        // Usar día 1 previene problemas de desbordamiento de fechas
        // (ej: 31 de febrero → 3 de marzo)
        const date = new Date(REFERENCE_YEAR_FOR_MONTH_NAMES, m - 1, 1);
        const monthName = date.toLocaleString('es-CL', { month: 'long' });
        return {
          value: String(m),
          label: toCapitalCase(monthName),
        };
      });
  }, [cutoffsWithinRange, filters.year]);

  // Agrupa los cortes por año y mes para el selector
  const filteredCutoffs = useMemo(() => {
    // Filtra por año y mes si corresponde
    const filtered = cutoffsWithinRange.filter(c => {
      const { year, month } = getCutoffYearMonth(c);
      if (filters.year && year !== filters.year) return false;
      if (filters.month && month !== filters.month) return false;
      return true;
    });

    // Devuelve lista plana de cortes
    return filtered.map(cut => ({
      value: String(cut.id),
      label: cut.label,
      supportingText: `${cut.startDate} - ${cut.endDate}`,
    }));
  }, [cutoffsWithinRange, filters.year, filters.month]);

  // Auto-select cut when only one option is available for the selected year/month
  useEffect(() => {
    if (
      filters.searchMode === 'MONTHLY' &&
      filters.year &&
      filters.month &&
      filteredCutoffs.length === 1 &&
      !filters.cutoffId
    ) {
      const singleCutId = Number(filteredCutoffs[0].value);
      if (singleCutId) {
        setFilters(previousFilters => ({ ...previousFilters, cutoffId: singleCutId }));
      }
    }
  }, [filters.searchMode, filters.year, filters.month, filters.cutoffId, filteredCutoffs]);

  /**
   * Carga los cortes visibles para el colaborador en la empresa indicada y reposiciona la
   * cascada Año/Mes/Descripción en el último corte creado (ASIS-5607).
   */
  const loadMonthlyCutoffs = useCallback(async (companyId: number) => {
    if (isLoadingCutoffsRef.current) return;
    isLoadingCutoffsRef.current = true;
    setLoadingCutoffs(true);
    try {
      const response = await attendanceService.getMonthlyCutsNew({
        filterCompanyId: companyId,
        employeeScoped: true,
      });

      if (response.success && response.data) {
        const cuts = response.data.cuts;
        setAllCutoffs(cuts);
        setNoCutsMessage(cuts.length === 0 ? translate('attendance:monthly.messages.noMonthlyCuts') : null);

        // El backend devuelve los cortes ordenados por fecha de inicio descendente:
        // el primero es el último corte creado disponible para esa empresa.
        // El año/mes se derivan con getCutoffYearMonth, la misma función que arma las opciones
        // de los selectores: usar cut.year/cut.month directamente dejaría preseleccionado un
        // valor que no existe en la lista cuando el corte cruza el cambio de mes.
        const latestCut = cuts[0];
        const latestPeriod = latestCut ? getCutoffYearMonth(latestCut) : null;
        setFilters(previousFilters => ({
          ...previousFilters,
          year: latestPeriod?.year ?? undefined,
          month: latestPeriod?.month ?? undefined,
          cutoffId: latestCut ? Number(latestCut.id) : undefined,
        }));
        return;
      }

      setAllCutoffs([]);
      setFilters(previousFilters => ({
        ...previousFilters,
        year: undefined,
        month: undefined,
        cutoffId: undefined,
      }));

      // Empresa sin cortes: se informa en pantalla (no como notificación) porque es un
      // estado esperado del filtro, no un fallo de la consulta.
      if (response.error?.code === 'NOT_FOUND_MONTHLY_CUTS') {
        setNoCutsMessage(
          response.error.detail || translate('attendance:monthly.messages.noMonthlyCuts'),
        );
      } else if (searchModeRef.current === 'MONTHLY') {
        setNoCutsMessage(null);
        showApiError(response, translate('attendance:errors.loadCutoffs'));
      }
    } catch {
      if (searchModeRef.current === 'MONTHLY') {
        showError(translate('attendance:errors.loadCutoffs'));
      }
    } finally {
      setLoadingCutoffs(false);
      isLoadingCutoffsRef.current = false;
    }
  }, [showApiError, showError, translate]);

  /** Carga inicial del filtro Empresa y preselección (ASIS-5607). */
  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      setLoadingCompanies(true);
      try {
        const response = await attendanceService.getEmployeeAttendanceCompanies(controller.signal);
        if (response.error?.code === 'ABORTED') return;

        if (response.success && response.data) {
          const availableCompanies = response.data.companies;
          setCompanies(availableCompanies);

          const preselected = response.data.defaultCompanyId ?? availableCompanies[0]?.companyId;
          if (preselected) {
            setFilters(previousFilters => ({ ...previousFilters, companyId: preselected }));
          }
          return;
        }

        setCompanies([]);
        if (response.error?.code === 'NOT_FOUND_EMPLOYEE_ATTENDANCE_COMPANIES') {
          setNoCutsMessage(translateRef.current('attendance:monthly.messages.noMonthlyCuts'));
        } else {
          showApiErrorRef.current(
            response,
            translateRef.current('attendance:monthly.messages.loadCompaniesError'),
          );
        }
      } finally {
        setLoadingCompanies(false);
      }
    })();

    return () => controller.abort();
    // Efecto de montaje: el catálogo de empresas no depende de ningún filtro. `translate` y
    // `showApiError` se leen por ref y no como dependencias — `translate` cambia de identidad
    // cuando i18n termina de inicializar, y como el cleanup aborta la petición en vuelo, cada
    // cambio de identidad cancelaría la carga y volvería a lanzarla, dejando el selector vacío.
  }, []);

  /** Recarga de cortes ante cada cambio de empresa. */
  useEffect(() => {
    if (!filters.companyId) return;
    if (loadedCutoffsCompanyRef.current === filters.companyId) return;

    loadedCutoffsCompanyRef.current = filters.companyId;
    void loadMonthlyCutoffs(filters.companyId);
  }, [filters.companyId, loadMonthlyCutoffs]);

  const canSearch = useMemo(() => validateCanSearch(filters), [filters]);

  const fetchData = useCallback(async (pageOverride?: number) => {
    if (!canSearch) return;
    if (isSearchingRef.current) return;
    
    isSearchingRef.current = true;
    setIsLoading(true);
    
    const targetPage = typeof pageOverride === 'number' ? pageOverride : page;

    try {
      const input: LC_GetMonthlyReportIn = {
        queryType: filters.searchMode === 'MONTHLY' ? 'MONTHLY_CUT' : 'DATE_RANGE',
        page: targetPage,
        pageSize,
      };

      if (filters.searchMode === 'MONTHLY') {
        const cutoff = allCutoffs.find(c => c.id === String(filters.cutoffId));
        if (cutoff) {
          input.monthlyCutCode = cutoff.id;
          // El filtro Empresa solo aplica en modo corte mensual: en modo rango el backend
          // resuelve la empresa desde el token.
          input.filterCompanyId = filters.companyId;
        } else {
          return;
        }
      } else {
        input.dateFrom = filters.dateFrom;
        input.dateTo = filters.dateTo;
      }

      const response = await attendanceService.getMonthlyReport(input);
      
      if (response.success && response.data) {
        setRows(response.data.days);
        setTotalRecords(response.data.pagination.totalRecords);
        setEmployeeInfo({
          name: response.data.header.employeeName,
          id: response.data.header.employeeRut,
        });
        setHasSearched(true);
      } else {
        if (response.status === 404 && response.error?.detail) {
          showApiError(response);
        } else {
          showApiError(response, translate('attendance:monthly.messages.noData'));
        }
        setRows([]);
        setTotalRecords(0);
      }
    } catch {
      showError(translate('common:messages.error'));
      setRows([]);
    } finally {
      setIsLoading(false);
      isSearchingRef.current = false;
    }
  }, [filters, page, pageSize, allCutoffs, canSearch, showApiError, showError, translate]);

  const getValue = useCallback((record: MonthlyReportDay, key: string) => {
    const raw = record.raw;
    const suffix = filters.formatType === 'DECIMAL' ? '_DEC' : '_TIME';
    
    const keyMap: Record<string, string> = {
      'agreedHours': 'agreedHours',
      'workedHours': 'workedHours',
      'overtime': 'totalOvertime',
      'absences': 'totalAbsences',
    };
    
    const baseKey = keyMap[key];
    if (baseKey) {
      const fullKey = `${baseKey}${suffix}`;
      return (raw.hours as any)[fullKey];
    }
    return (record as any)[key];
  }, [filters.formatType]);

  const columns: TableColumn<MonthlyReportDay>[] = useMemo(() => [
    {
      // Fecha y día van fusionados en una sola columna ("15/06/2026 (L)").
      // El ordenamiento sigue usando `date`, que conserva el valor original.
      key: 'date',
      header: translate('attendance:monthly.table.date'),
      sortable: true,
      render: (record) => formatDateWithDayInitial(record.date),
    },
    {
      key: 'checkIn',
      header: translate('attendance:monthly.table.checkIn'),
      align: 'left',
      sortable: true,
    },
    {
      key: 'checkOut',
      header: translate('attendance:monthly.table.checkOut'),
      align: 'left',
      sortable: true,
    },
    {
      key: 'agreedHours',
      header: translate('attendance:monthly.table.agreedHours'),
      align: 'left',
      sortable: true,
      headerInfoTitle: translate('attendance:monthly.table.hoursInfo.agreedHoursTitle'),
      headerInfoText: translate('attendance:monthly.table.hoursInfo.agreedHours'),
      render: (record) => getValue(record, 'agreedHours'),
    },
    {
      key: 'workedHours',
      header: translate('attendance:monthly.table.workedHours'),
      align: 'left',
      sortable: true,
      headerInfoTitle: translate('attendance:monthly.table.hoursInfo.workedHoursTitle'),
      headerInfoText: translate('attendance:monthly.table.hoursInfo.workedHours'),
      render: (record) => getValue(record, 'workedHours'),
    },
    {
      key: 'overtime',
      header: translate('attendance:monthly.table.overtime'),
      align: 'left',
      sortable: true,
      headerInfoTitle: translate('attendance:monthly.table.hoursInfo.overtimeTitle'),
      headerInfoText: translate('attendance:monthly.table.hoursInfo.overtime'),
      render: (record) => getValue(record, 'overtime'),
    },
    {
      key: 'absences',
      header: translate('attendance:monthly.table.absences'),
      align: 'left',
      sortable: true,
      headerInfoTitle: translate('attendance:monthly.table.hoursInfo.absencesTitle'),
      headerInfoText: translate('attendance:monthly.table.hoursInfo.absences'),
      render: (record) => getValue(record, 'absences'),
    },
  ], [translate, getValue]);

  const actions: TableAction<MonthlyReportDay>[] = useMemo(() => [], []);

  const handleRangeTypeChange = useCallback((rangeType: RangeType) => {
    const { dateFrom, dateTo } = calculateDateRange(
      rangeType,
      formatDateToYYYYMMDD,
    );
    setFilters(previousFilters => ({
      ...previousFilters,
      rangeType,
      dateFrom,
      dateTo,
    }));
    setPage(1);
  }, []);

  const { onExport: exportFile, isExporting } = useExport<UIExportFormat>({
    exportFn: (exportFormat) => {
      const queryType = filters.searchMode === 'MONTHLY' ? 'MONTHLY_CUT' : 'DATE_RANGE';
      let monthlyCutCode: string | undefined;
      let dateFrom: string | undefined;
      let dateTo: string | undefined;

      if (filters.searchMode === 'MONTHLY') {
        const cutoff = allCutoffs.find(c => c.id === String(filters.cutoffId));
        monthlyCutCode = cutoff?.id;
      } else {
        dateFrom = filters.dateFrom;
        dateTo = filters.dateTo;
      }

      return attendanceService.exportMonthlyReport(
        queryType,
        monthlyCutCode,
        dateFrom,
        dateTo,
        (filters.formatType as 'HH:MM:SS' | 'DECIMAL') || 'HH:MM:SS',
        exportFormat,
        filters.searchMode === 'MONTHLY' ? filters.companyId : undefined,
      );
    },
    successMessage: translate('attendance:monthly.messages.exportSuccess'),
    errorMessage: translate('attendance:monthly.messages.exportError'),
  });

  const handleExport = useCallback(async (exportFormat: UIExportFormat) => {
    if (!hasSearched) {
      showError(translate('attendance:monthly.messages.selectAllFilters'));
      return;
    }
    await exportFile(exportFormat);
  }, [exportFile, hasSearched, showError, translate]);

  const openExportModal = useCallback(() => {
    openModal('ExportModal', {
      onExport: handleExport,
    });
  }, [openModal, handleExport]);

  const filteredRows = useMemo(() => {
    if (!debouncedSearchText) return rows;
    const lower = debouncedSearchText.toLowerCase();
    return rows.filter(row =>
      row.date.toLowerCase().includes(lower) ||
      row.dayName.toLowerCase().includes(lower) ||
      row.comments.toLowerCase().includes(lower),
    );
  }, [rows, debouncedSearchText]);

  useEffect(() => {
    if (hasSearched) {
      void (async () => { await fetchData(); })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // ASIS-5607: el botón Buscar desaparece y la tabla se refresca sola ante cualquier cambio
  // de los filtros principales. El debounce agrupa la cascada Empresa -> Año -> Mes ->
  // Descripción en una única consulta.
  useEffect(() => {
    if (!canSearch) {
      setRows([]);
      setTotalRecords(0);
      return;
    }

    const timeoutId = setTimeout(() => {
      setPage(1);
      void fetchData(1);
    }, AUTO_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
    // fetchData se recrea con cada cambio de `filters`; depender de los campos evita
    // reprogramar el timeout por identidades nuevas de la función.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canSearch,
    filters.searchMode,
    filters.companyId,
    filters.year,
    filters.month,
    filters.cutoffId,
    filters.dateFrom,
    filters.dateTo,
  ]);

  const hasData = useMemo(() => filteredRows.length > 0, [filteredRows]);

  return {
    filters,
    setFilters,
    rows: filteredRows,
    totalRecords,
    employeeInfo,
    page,
    pageSize,
    isLoading: isLoading || isExporting,
    hasSearched,
    hasData,
    cutoffs: filteredCutoffs,
    loadingCutoffs,
    companies,
    loadingCompanies,
    noCutsMessage,
    canSearch,
    fetchData,
    setPage,
    setPageSize,
    onSearch: setSearchText,
    getValue,
    handleRangeTypeChange,
    handleExport,
    openExportModal,
    columns,
    actions,
    years,
    months,
  };
};
